import { useMemo } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { byHoldTime, byTimeSlot } from "../../lib/analytics";
import { closedOnly, groupBy, summarize } from "../../lib/stats";
import { BucketTable, Insights } from "../../ui/analytics";
import { PnlBars } from "../../ui/Charts";
import { HourHeatmap } from "../../ui/Heatmap";
import { Card } from "../../ui/ui";
import { compact, insight, type TabProps } from "./shared";

export function TimingTab({ trades }: TabProps) {
  const tl = useTranslation().lite;
  const t = tl.stats.timing;
  const { fmt } = useLiteData();

  const slots = byTimeSlot(trades).map(b => ({ ...b, label: tl.stats.buckets.slot[b.key] }));
  const hold = byHoldTime(trades).map(b => ({ ...b, label: tl.stats.buckets.hold[b.key] }));
  const weekday = useMemo(() => {
    const g = groupBy(closedOnly(trades), tr => (new Date(tr.openedAt).getDay() + 6) % 7);
    return tl.common.weekdaysLong
      .map((label, i) => ({ key: String(i), label, short: tl.common.weekdaysShort[i], s: summarize(g.get(i) ?? []) }))
      .filter(b => b.s.count);
  }, [trades, tl]);
  const hours = useMemo(() => {
    const g = groupBy(closedOnly(trades), tr => new Date(tr.openedAt).getHours());
    return [...g.keys()].sort((a, b) => a - b).map(h => ({ key: String(h), label: tl.common.hour(h), s: summarize(g.get(h) ?? []) }));
  }, [trades, tl]);

  const bestSlot = [...slots].sort((a, b) => b.s.netPnl - a.s.netPnl)[0];
  const worstSlot = [...slots].sort((a, b) => a.s.netPnl - b.s.netPnl)[0];
  const worstDay = [...weekday].sort((a, b) => a.s.netPnl - b.s.netPnl)[0];
  const bestHold = [...hold].sort((a, b) => b.s.expectancy - a.s.expectancy)[0];

  const insights = compact([
    bestSlot && insight("good", t.insightBestSlot(bestSlot.label, fmt.money(bestSlot.s.netPnl), fmt.pct(bestSlot.s.winRate))),
    worstSlot && worstSlot.s.netPnl < 0 && insight("bad", t.insightWorstSlot(worstSlot.label, fmt.money(worstSlot.s.netPnl, { sign: false }))),
    worstDay && worstDay.s.netPnl < 0 && insight("bad", t.insightWorstDay(worstDay.label, fmt.money(worstDay.s.netPnl))),
    bestHold && insight("info", t.insightBestHold(bestHold.label, fmt.money(bestHold.s.expectancy))),
  ]);

  return (
    <>
      <Insights items={insights} />
      <div className="grid xl:grid-cols-2 gap-5">
        <Card title={t.byWeekday}><PnlBars data={weekday.map(w => ({ label: w.short, value: Math.round(w.s.netPnl) }))} height={220} /></Card>
        <Card title={t.byHour}><PnlBars data={hours.map(h => ({ label: h.label, value: Math.round(h.s.netPnl) }))} height={220} /></Card>
      </div>
      <Card title={t.heatmap} subtitle={t.heatmapSubtitle}><HourHeatmap trades={trades} /></Card>
      <div className="grid xl:grid-cols-2 gap-5">
        <BucketTable title={t.bySlot} subtitle={t.bySlotSubtitle} rows={slots} />
        <BucketTable title={t.byHold} rows={hold} />
      </div>
      <BucketTable title={t.byWeekdayDetail} rows={weekday} />
    </>
  );
}
